// apps/site-dna-web/src/components/ev/FleetBuilder.js
/**
 * FleetBuilder - Manages the fleet of vehicle groups
 *
 * Displays list of VehicleCards with add/remove functionality.
 * Fetches vehicle database from API on mount.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Add as AddIcon, LocalShipping as FleetIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useAuth, useResponsive, vortxColors, SectionCard } from '@vortx/shared-ui';
import VehicleCard from './VehicleCard';
import * as evService from '@vortx/shared-ui/src/services/evProjectService';

/**
 * FleetBuilder Component
 *
 * @param {Object} props
 * @param {Array} props.fleet - Array of vehicle groups
 * @param {Function} props.onChange - Callback with updated fleet array
 */
function FleetBuilder({ fleet = [], onChange, onUploadClick }) {
    const { currentUser } = useAuth();
    const { isMobile } = useResponsive();
    const [vehicleDatabase, setVehicleDatabase] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load vehicle database on mount
    useEffect(() => {
        if (!currentUser) return;

        const loadVehicles = async () => {
            try {
                setLoading(true);
                const data = await evService.getVehicleDatabase(currentUser);
                setVehicleDatabase(data.vehicles || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadVehicles();
    }, [currentUser]);

    const handleAddVehicle = () => {
        const newVehicle = {
            id: `vehicle_${Date.now()}`,
            vehicle_id: null,
            vehicle_name: null,
            quantity: 1,
            daily_miles: 100,
            departure_time: '06:00',
            return_time: '18:00',
            battery_capacity_kwh: 100,
            efficiency_kwh_per_mile: 0.5,
            max_ac_charge_kw: 19.2,
            max_dc_charge_kw: 150,
        };
        onChange([...fleet, newVehicle]);
    };

    const handleUpdateVehicle = (index, updatedVehicle) => {
        const newFleet = [...fleet];
        newFleet[index] = updatedVehicle;
        onChange(newFleet);
    };

    const handleRemoveVehicle = (index) => {
        const newFleet = fleet.filter((_, i) => i !== index);
        onChange(newFleet);
    };

    // Calculate fleet summary
    const totalVehicles = fleet.reduce((sum, v) => sum + (v.quantity || 0), 0);
    const totalDailyEnergy = fleet.reduce((sum, v) => {
        const qty = v.quantity || 0;
        const miles = v.daily_miles || 0;
        const eff = v.efficiency_kwh_per_mile || 0.5;
        return sum + (qty * miles * eff);
    }, 0);

    if (loading) {
        return (
            <SectionCard title="Fleet" icon={<FleetIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                </Box>
            </SectionCard>
        );
    }

    return (
        <SectionCard
            title="Fleet"
            icon={<FleetIcon />}
            action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {onUploadClick && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            onClick={onUploadClick}
                            sx={{
                                borderColor: vortxColors.gold,
                                color: vortxColors.gold,
                                '&:hover': { borderColor: vortxColors.gold, bgcolor: 'rgba(178,140,80,0.1)' },
                            }}
                        >
                            {isMobile ? 'Upload' : 'Upload Fleet Data'}
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddVehicle}
                        sx={{
                            borderColor: vortxColors.gold,
                            color: vortxColors.gold,
                            '&:hover': { borderColor: vortxColors.gold, bgcolor: 'rgba(178,140,80,0.1)' },
                        }}
                    >
                        {isMobile ? 'Add' : 'Add Vehicle'}
                    </Button>
                </Box>
            }
        >
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Fleet Summary */}
            {fleet.length > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{totalVehicles}</strong> vehicles • <strong>{Math.round(totalDailyEnergy).toLocaleString()}</strong> kWh daily energy
                    </Typography>
                </Box>
            )}

            {/* Clear imported vehicles */}
            {fleet.some(v => v.source === 'fleet_operations_import') && (
                <Button
                    size="small"
                    onClick={() => onChange(fleet.filter(v => v.source !== 'fleet_operations_import'))}
                    sx={{ color: vortxColors.stone, textTransform: 'none', mb: 1 }}
                >
                    Clear {fleet.filter(v => v.source === 'fleet_operations_import').length} imported vehicles
                </Button>
            )}

            {/* Vehicle Cards */}
            {fleet.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <FleetIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary" gutterBottom>
                        No vehicles in fleet
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddVehicle}
                    >
                        Add First Vehicle
                    </Button>
                </Box>
            ) : (
                fleet.map((vehicle, index) => (
                    <VehicleCard
                        key={vehicle.id || index}
                        index={index}
                        vehicle={vehicle}
                        vehicleDatabase={vehicleDatabase}
                        onChange={(updated) => handleUpdateVehicle(index, updated)}
                        onRemove={() => handleRemoveVehicle(index)}
                    />
                ))
            )}
        </SectionCard>
    );
}

export default FleetBuilder;
